import React, { useCallback, useState } from "react";
import styled from "styled-components";
import { AmplitudeService } from "@analytics/Amplitude/AmplitudeService";
import * as GA4Service from "@analytics/GoogleAnalytics/GA4Service";
import LifeAtButton from "@common/LifeAtButton/LifeAtButton";
import LifeAtCheckbox from "@common/LifeAtCheckbox/LifeAtCheckbox";
import LoadingSpinner from "@common/LoadingSpinner/LoadingSpinner";
import { UserContextType, useUserContext } from "@contexts/UserContext";
import { functions } from "@firebase-local/firebase";
import { callMonolith } from "@service/ServerlessFunctionsService";
import { BrowserUtil } from "@util/index";
import InfoPageTemplate from "./templates/InfoPageTemplate";

const InfoPageStyled = styled(InfoPageTemplate)`
    padding: 25px 50px;
    height: 100%;
`;

const SubHeader = styled.div`
    width: 100%;
    font-size: 20px;
    margin-bottom: 36px;
    color: ${({ theme }) => theme.textColorPrimary};
`;

const DeleteActionWrapper = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    margin: 20px 0;
`;

const CheckboxWrapper = styled.div`
    margin-bottom: 20px;
`;

const StyledLabel = styled.label`
    color: ${({ theme }) => theme.textColorPrimary};
`;

const DeleteButton = styled(LifeAtButton)`
    width: fit-content;

    border: none !important;
    background-color: #e39685 !important;
    color: white !important;
    * {
        background-color: #e39685 !important;
        color: white !important;
    }
`;

const SUB_HEADER_LABEL =
    "Please take a moment to fill the survey below to help the LifeAt team. Then scroll below to complete the process.";
const CONFIRMATION_LABEL =
    "By checking this box and continuing, you will permanently lose all data associated with your account. There is no turning back.";
const DELETE_BUTTON_TEXT = "Confirm Deletion";
const DELETE_SUCCESS_TEXT = "Your account has been successfully deleted";
const DELETE_ERROR_MESSAGE =
    `There was an error deleting your account. ` +
    `\n\nPlease try again in a few minutes. If the problem persists request ` +
    `support by emailing contact@lifeat.io (or messaging in Discord) with this ` +
    `error message. Thank you!`;

function DeleteAccountPage() {
    const amplitudeService = AmplitudeService.getInstance();

    const { user }: UserContextType = useUserContext();

    const [deleteChecked, setDeleteChecked] = useState(false);
    const [deleteCompleted, setDeleteCompleted] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSurveyLoading, setIsSurveyLoading] = useState(true);

    const handleDelete = useCallback(() => {
        if (user && user.email) {
            setIsLoading(true);

            GA4Service.unsetUserId();
            amplitudeService.unsetUserId();

            callMonolith("delete-user", { userEmail: user.email })
                .then(() => {
                    setDeleteCompleted(true);
                    alert("Your account has been successfully deleted. You will now be redirected. Goodbye.");
                    BrowserUtil.redirectTo("https://lifeat.io");
                })
                .catch((error) => {
                    alert(`${DELETE_ERROR_MESSAGE}\n\nError: ${error?.message}`);
                    setIsLoading(false);
                });
        }
    }, [user]);

    const deleteActBtn = (
        <DeleteButton isLink={false} text={DELETE_BUTTON_TEXT} handleClick={handleDelete} disabled={!deleteChecked} />
    );

    const DeleteActions = () => {
        if (deleteCompleted) {
            return <StyledLabel>{DELETE_SUCCESS_TEXT}</StyledLabel>;
        } else {
            return (
                <>
                    <CheckboxWrapper className={`delete-account-checkbox-wrapper`}>
                        <LifeAtCheckbox
                            checked={deleteChecked}
                            handleToggle={() => setDeleteChecked(!deleteChecked)}
                            className={`delete-account-checkbox`}
                            id={`delete-account-checkbox`}
                        />
                        <StyledLabel>{CONFIRMATION_LABEL}</StyledLabel>
                    </CheckboxWrapper>

                    {isLoading || isSurveyLoading ? <LoadingSpinner size={20} /> : deleteActBtn}
                </>
            );
        }
    };

    return (
        <InfoPageStyled showNavBarOverride={false}>
            <SubHeader>{SUB_HEADER_LABEL}</SubHeader>

            <iframe
                className="airtable-embed"
                src="https://airtable.com/embed/shr7A0jBxRNiRdnxP?backgroundColor=red"
                frameBorder="1"
                width="100%"
                height="1150"
                onLoad={() => setIsSurveyLoading(false)}
            />

            {user && (
                <DeleteActionWrapper>
                    <DeleteActions />
                </DeleteActionWrapper>
            )}
        </InfoPageStyled>
    );
}

export default DeleteAccountPage;
